// Admin Content Management Page (Updates & Testimonials)
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import GlassCard from '../../components/admin/GlassCard';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';

const AdminContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'updates' | 'testimonials'>('updates');
  const [updates, setUpdates] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    excerpt: '',
    content: '',
    image_url: '',
    date: new Date().toISOString().split('T')[0],
    author: 'Admin',
    tags: '',
    featured: false,
    // Testimonial fields
    testimonial_author: '',
    position: '',
    rating: '5'
  });
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string>('');
  const [uploading, setUploading] = React.useState(false);

  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'updates') {
        const response = await fetch('http://localhost:5000/api/updates');
        const data = await response.json();
        setUpdates(Array.isArray(data) ? data : []);
      } else {
        const response = await fetch('http://localhost:5000/api/testimonials');
        const data = await response.json();
        setTestimonials(Array.isArray(data) ? data : []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      let imageUrl = formData.image_url;

      // Upload image if a new file is selected
      if (imageFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('image', imageFile);
        uploadFormData.append('category', activeTab === 'updates' ? 'updates' : 'testimonials');

        const uploadResponse = await fetch('http://localhost:5000/api/upload', {
          method: 'POST',
          body: uploadFormData
        });

        if (!uploadResponse.ok) {
          throw new Error('Image upload failed');
        }

        const uploadResult = await uploadResponse.json();
        imageUrl = uploadResult.url;
      }

      const endpoint = activeTab === 'updates' ? 'updates' : 'testimonials';
      const url = editingItem
        ? `http://localhost:5000/api/${endpoint}/${editingItem.id}`
        : `http://localhost:5000/api/${endpoint}`;
      
      const body = activeTab === 'updates'
        ? { 
            title: formData.title,
            category: formData.category,
            image: imageUrl,
            excerpt: formData.excerpt,
            content: formData.content,
            date: formData.date,
            author: formData.author,
            tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
            featured: formData.featured
          }
        : { 
            name: formData.testimonial_author,
            role: formData.position,
            message: formData.content,
            image: imageUrl,
            rating: parseInt(formData.rating),
            featured: formData.featured,
            order_position: 0,
            active: true
          };

      console.log('Submitting to:', url);
      console.log('Body:', body);
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (response.ok) {
        alert(`${activeTab === 'updates' ? 'Update' : 'Testimonial'} ${editingItem ? 'updated' : 'added'} successfully!`);
        fetchData();
        setShowAddForm(false);
        setEditingItem(null);
        setImageFile(null);
        setImagePreview('');
        setFormData({
          title: '',
          category: '',
          excerpt: '',
          content: '',
          image_url: '',
          date: new Date().toISOString().split('T')[0],
          author: 'Admin',
          tags: '',
          featured: false,
          testimonial_author: '',
          position: '',
          rating: '5'
        });
      } else {
        throw new Error(responseData.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    if (activeTab === 'updates') {
      setFormData({
        title: item.title,
        category: item.category || '',
        excerpt: item.excerpt || '',
        content: item.content,
        image_url: item.image || '',
        date: item.date ? item.date.split('T')[0] : new Date().toISOString().split('T')[0],
        author: item.author || 'Admin',
        tags: Array.isArray(item.tags) ? item.tags.join(', ') : '',
        featured: item.featured || false,
        testimonial_author: '',
        position: '',
        rating: '5'
      });
      setImagePreview(item.image ? (item.image.startsWith('http') ? item.image : `http://localhost:3000${item.image}`) : '');
      setImageFile(null);
    } else {
      setFormData({
        title: '',
        category: '',
        excerpt: '',
        content: item.message || '',
        image_url: item.image || '',
        date: new Date().toISOString().split('T')[0],
        author: 'Admin',
        tags: '',
        featured: false,
        testimonial_author: item.name || '',
        position: item.role || '',
        rating: item.rating ? item.rating.toString() : '5'
      });
      setImagePreview(item.image ? (item.image.startsWith('http') ? item.image : `http://localhost:3000${item.image}`) : '');
      setImageFile(null);
    }
    setShowAddForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const endpoint = activeTab === 'updates' ? 'updates' : 'testimonials';
        await fetch(`http://localhost:5000/api/${endpoint}/${id}`, {
          method: 'DELETE'
        });
        fetchData();
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  const updateColumns = [
    { key: 'title', label: 'Title', sortable: true },
    { key: 'category', label: 'Category', sortable: true },
    {
      key: 'excerpt',
      label: 'Excerpt',
      render: (value: string) => value ? value.substring(0, 40) + '...' : 'N/A'
    },
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
      key: 'featured',
      label: 'Featured',
      render: (value: boolean) => (
        <span style={{
          padding: '3px 8px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          background: value ? '#D1FAE5' : '#F3F4F6',
          color: value ? '#065F46' : '#6B7280'
        }}>
          {value ? '⭐ Yes' : 'No'}
        </span>
      )
    }
  ];

  const testimonialColumns = [
    { 
      key: 'name',
      label: 'Author',
      sortable: true
    },
    { 
      key: 'role',
      label: 'Position',
      sortable: true
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (value: number) => '⭐'.repeat(value || 5)
    },
    {
      key: 'message',
      label: 'Content',
      render: (value: string) => value ? value.substring(0, 40) + '...' : 'N/A'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
            Content Management
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
            Manage updates and testimonials
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingItem(null);
            setImageFile(null);
            setImagePreview('');
            setFormData({
              title: '',
              category: '',
              excerpt: '',
              content: '',
              image_url: '',
              date: new Date().toISOString().split('T')[0],
              author: 'Admin',
              tags: '',
              featured: false,
              testimonial_author: '',
              position: '',
              rating: '5'
            });
          }}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#111827',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          {showAddForm ? '✕ Cancel' : `+ Add ${activeTab === 'updates' ? 'Update' : 'Testimonial'}`}
        </button>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '8px' }}>
        <button
          onClick={() => {
            setActiveTab('updates');
            setShowAddForm(false);
            setEditingItem(null);
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: activeTab === 'updates' ? 'none' : '1px solid #E5E7EB',
            background: activeTab === 'updates' ? '#111827' : '#FFFFFF',
            color: activeTab === 'updates' ? '#FFFFFF' : '#6B7280',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Updates
        </button>
        <button
          onClick={() => {
            setActiveTab('testimonials');
            setShowAddForm(false);
            setEditingItem(null);
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: activeTab === 'testimonials' ? 'none' : '1px solid #E5E7EB',
            background: activeTab === 'testimonials' ? '#111827' : '#FFFFFF',
            color: activeTab === 'testimonials' ? '#FFFFFF' : '#6B7280',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Testimonials
        </button>
      </div>

      {showAddForm && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '16px', letterSpacing: '-0.01em' }}>
            {editingItem ? `Edit ${activeTab === 'updates' ? 'Update' : 'Testimonial'}` : `Add New ${activeTab === 'updates' ? 'Update' : 'Testimonial'}`}
          </h3>
          <form onSubmit={handleSubmit}>
            {activeTab === 'updates' ? (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select Category</option>
                      <option value="Education">Education</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Community Development">Community Development</option>
                      <option value="Events">Events</option>
                      <option value="News">News</option>
                      <option value="Success Story">Success Story</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                      Date *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Update Image (Auto-resized to 310 x 224px) *
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={!editingItem}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#111827',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '12px' }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: '310px',
                          height: '224px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #E5E7EB'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Excerpt (Short summary) *
                  </label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    required
                    rows={2}
                    placeholder="Brief description that appears on the card (max 150 characters)"
                    maxLength={150}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  <small style={{ color: '#9CA3AF', fontSize: '11px' }}>
                    {formData.excerpt.length}/150 characters
                  </small>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                      Author *
                    </label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="e.g., education, children, project"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ color: '#6B7280', fontSize: '13px', fontWeight: '600' }}>
                      Mark as Featured (will appear on homepage)
                    </span>
                  </label>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                      Author Name *
                    </label>
                    <input
                      type="text"
                      value={formData.testimonial_author}
                      onChange={(e) => setFormData({ ...formData, testimonial_author: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                      Position/Role *
                    </label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      required
                      placeholder="e.g., Parent, Donor, Volunteer"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Profile Image (Auto-resized to 370 x 451px)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#111827',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '12px' }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: '185px',
                          height: '225.5px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #E5E7EB'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                    Rating
                  </label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  >
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="2">2 Stars</option>
                    <option value="1">1 Star</option>
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ color: '#6B7280', fontSize: '13px', fontWeight: '600' }}>
                      Show on Homepage (Featured)
                    </span>
                  </label>
                </div>
              </>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#6B7280', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>
                {activeTab === 'updates' ? 'Full Content *' : 'Testimonial Text *'}
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                rows={activeTab === 'updates' ? 8 : 6}
                placeholder={activeTab === 'updates' ? 'Write the full update content here...' : 'Write the testimonial here...'}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '13px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={uploading}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                background: uploading ? '#9CA3AF' : '#111827',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.7 : 1
              }}
            >
              {uploading ? 'Uploading...' : (editingItem ? `Update ${activeTab === 'updates' ? 'Update' : 'Testimonial'}` : `Add ${activeTab === 'updates' ? 'Update' : 'Testimonial'}`)}
            </button>
          </form>
        </GlassCard>
      )}

      <DataTable
        columns={activeTab === 'updates' ? updateColumns : testimonialColumns}
        data={activeTab === 'updates' ? updates : testimonials}
        actions={(row) => (
          <>
            <button
              onClick={() => handleEdit(row)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #DBEAFE',
                background: '#EFF6FF',
                color: '#2563EB',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #FEE2E2',
                background: '#FEF2F2',
                color: '#DC2626',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          </>
        )}
      />
    </AdminLayout>
  );
};

export default AdminContent;
